import { type AppDispatch, type RootState } from "features/Application/globaltypes/redux";
import { connect, type ConnectedProps } from "react-redux";
import { GroupTemplatePacks } from "../Details/Packs/GroupTemplatePacks";
import styles from "../Details/Packs/groupTemplatePacks.module.scss";
import { RouteNames, type SortingDirection } from "enums";
import { getPacks } from "../state/thunks/TemplatePacksThunk";
import { bindAction } from "interfaces";
import { noop } from "lodash";
import { type ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "components/buttons/button/Button";
import { useNavigate } from "react-router";
import AddPacksModal from "./Packs/AddPacksModal";
import { fetchAddOns } from "features/Accounts/state/thunks/addOnsThunk";
import { getCustomers } from "../state/thunks/TemplateCustomersThunk";
import { fetchAvailablePacks } from "../state/thunks/TemplateAvailablePacksThunk";
import { reset } from "../state/TemplatePacksSlice";

type Props = PropsFromRedux & {
  templateId: number;
  selectedIds: number[];
  setSelectedIds: (ids: number[]) => void;
  onRemovePackClick: (id: number) => void;
  setPacksTabActionButton?: (actionButton: ReactNode) => void;
  preview?: boolean;
  disabled?: boolean;
};

export const EditTemplatePacks = (props: Props) => {
  const {
    templateId,
    items,
    itemsCount,
    isLoading,
    getData,
    selectedIds,
    setSelectedIds,
    onRemovePackClick,
    preview,
    disabled,
    setPacksTabActionButton,
    fetchPacks,
    ownPackCount,
    packRequestError,
    accountId,
    fetchAddOns,
    getCustomersAction,
    reset,
  } = props;
  const [showAddPacksModal, setShowAddPacksModal] = useState(false);
  const navigate = useNavigate();
  const loadPacks = (skip: number, top: number, sortingColumnName: string, sortingDirection: SortingDirection) => {
    getData(templateId, skip, top, sortingColumnName, sortingDirection);
  };

  useEffect(() => {
    return () => {
      reset();
    };
  }, [reset]);

  useEffect(() => {
    if (!showAddPacksModal) {
      fetchPacks({ templateGroupId: templateId, skip: 0 });
    }
  }, [fetchPacks, showAddPacksModal, templateId]);

  useEffect(() => {
    fetchAddOns(accountId);
  }, [fetchAddOns, accountId]);

  const hasOwnPacks = !!packRequestError || ownPackCount > 0;

  const handleAddPacksClick = useCallback(async () => {
    await getCustomersAction(templateId, 0, 100000);
    setShowAddPacksModal(true);
  }, [getCustomersAction, templateId]);

  const actionButton = useMemo<ReactNode>(() => {
    if (disabled || preview) {
      return null;
    }
    if (!hasOwnPacks) {
      return (
        <Button
          className={styles["create-pack-button"]}
          primary
          onClick={() => navigate(`/${RouteNames.licensingPacks}/create`)}
        >
          Create Pack
        </Button>
      );
    }
    return (
      <Button className={styles["create-pack-button"]} primary onClick={handleAddPacksClick}>
        Add Packs
      </Button>
    );
  }, [disabled, preview, hasOwnPacks, handleAddPacksClick, navigate]);

  useEffect(() => {
    setPacksTabActionButton?.(actionButton);
  }, [actionButton, setPacksTabActionButton]);

  return (
    <>
      <GroupTemplatePacks
        templateId={templateId}
        items={items}
        itemsCount={itemsCount}
        isLoading={isLoading}
        getGridData={noop}
        getListData={loadPacks}
        selectedIds={selectedIds}
        setSelectedIds={setSelectedIds}
        onRemovePackClick={onRemovePackClick}
        actionButton={actionButton}
        ownsPacks={hasOwnPacks}
        isCreate={false}
        disabled={disabled}
        preview={preview}
      />
      <AddPacksModal showModal={showAddPacksModal} setShowModal={setShowAddPacksModal} templateId={templateId} />
    </>
  );
};

/* istanbul ignore next */
const mapStateToProps = (state: RootState) => {
  return {
    items: state.people.groupTemplate.packs.items,
    itemsCount: state.people.groupTemplate.packs.itemsCount,
    isLoading: state.people.groupTemplate.packs.isLoading,
    ownPackCount: state.people.groupTemplate.availablePacks.itemsCount,
    packRequestError: state.people.groupTemplate.availablePacks.error,
    accountId: state.userProfile.accountId,
  };
};

const mapDispatchToProps = (dispatch: AppDispatch) => {
  return {
    getCustomersAction: bindAction(getCustomers, dispatch),
    getData: bindAction(getPacks, dispatch),
    fetchPacks: bindAction(fetchAvailablePacks, dispatch),
    fetchAddOns: bindAction(fetchAddOns, dispatch),
    reset: bindAction(reset, dispatch),
  };
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(EditTemplatePacks);
export default ConnectedComponent;
